import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { seedDatabase } from '../services/seedService';
import { useEnvironmentMode } from '../services/productionModeService';
import { PurgeMockDataModal } from '../components/admin/PurgeMockDataModal';
import { db } from '../services/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isProduction } = useEnvironmentMode();
  const [isPurgeModalOpen, setIsPurgeModalOpen] = useState(false);

  const [stats, setStats] = useState({
    totalItems: 0,
    totalRepairs: 0,
    openClaims: 0,
    openIncidents: 0
  });

  const fetchStats = async () => {
    try {
      const itemsSnap = await getDocs(query(collection(db, 'verisure_items')));
      const repairsSnap = await getDocs(query(collection(db, 'verisure_repairs')));
      const claimsSnap = await getDocs(query(collection(db, 'verisure_claims'), where('status', '==', 'pending')));
      
      // Legacy incident ticket fallback for now
      const ticketsSnap = await getDocs(query(collection(db, 'tickets'), where('status', '==', 'open')));

      setStats({
        totalItems: itemsSnap.size,
        totalRepairs: repairsSnap.size,
        openClaims: claimsSnap.size,
        openIncidents: ticketsSnap.size
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const isAdmin = ['super_admin', 'master_admin'].includes(user?.role || '');

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-7xl mx-auto font-sans space-y-6 sm:space-y-8 min-w-0 max-w-full overflow-hidden">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 sm:gap-6 min-w-0 max-w-full">
        <div className="min-w-0 max-w-full">
          <div className="inline-flex items-center gap-2 px-3 py-1 glass-pill-container text-orange-400 text-[11px] font-bold rounded-full mb-3 shadow-sm border border-orange-500/25">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            <span className="tracking-wider uppercase">VERISURE COMMAND & AUDIT NETWORK</span>
          </div>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-extrabold text-white tracking-tight flex items-center gap-2.5 sm:gap-3">
            <span>Command Center</span>
            <button
              onClick={fetchStats}
              className="p-1.5 sm:p-2 glass-pill-container hover:bg-white/10 text-slate-300 rounded-full transition-all border border-white/10 active:scale-95"
              title="Refresh Telemetry"
            >
              <svg className="w-3.5 sm:w-4 h-3.5 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </h1>
          <p className="text-slate-400 text-xs sm:text-sm mt-1.5 sm:mt-2 max-w-xl font-normal leading-relaxed">
            Welcome back, <strong className="text-white font-semibold">{user?.name}</strong> &bull; Authenticated as{' '}
            <span className="ml-1 glass-pill-container text-orange-400 font-mono text-[10px] sm:text-[11px] px-2 sm:px-2.5 py-0.5 rounded-full border border-orange-500/30 uppercase font-bold">
              {user?.role.replace('_', ' ')}
            </span>
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full md:w-auto">
          <button
            onClick={() => navigate('/tag')}
            className="px-3.5 sm:px-4 py-2 sm:py-2.5 rounded-full text-xs font-bold text-white bg-gradient-to-r from-red-600 via-orange-500 to-amber-500 hover:opacity-95 transition-all flex items-center gap-1.5 sm:gap-2 shadow-lg shadow-orange-500/25 active:scale-95 shrink-0"
            title="Register a device and generate an official Verisure QR badge"
          >
            <span>+</span>
            <span>Register Device & QR</span>
          </button>
          {isAdmin && (
            <button
              onClick={() => navigate('/admin/blank-qr')}
              className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-full text-xs font-bold text-orange-400 glass-pill-container border border-orange-500/40 hover:bg-orange-500/10 transition-all flex items-center gap-1.5 sm:gap-2 shadow-sm shrink-0"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                <line x1="7" y1="7" x2="7.01" y2="7"/>
              </svg>
              <span>Blank Outlet QRs</span>
            </button>
          )}
          {isAdmin && (
            <button
              onClick={() => setIsPurgeModalOpen(true)}
              className={`px-3 sm:px-4 py-2 sm:py-2.5 rounded-full text-xs font-bold transition-all flex items-center gap-1.5 sm:gap-2 shadow-sm border shrink-0 ${
                isProduction
                  ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/25'
                  : 'bg-amber-500/15 text-amber-400 border-amber-500/30 hover:bg-amber-500/25'
              }`}
              title="Manage environment mode and purge mock data"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${isProduction ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'}`}></span>
              <span>{isProduction ? 'Live Production Mode' : 'Purge Mock Data'}</span>
            </button>
          )}
          {user?.role === 'super_admin' && !isProduction && (
            <button
              onClick={seedDatabase}
              className="px-3 sm:px-4 py-2 sm:py-2.5 rounded-full text-xs font-bold text-slate-300 glass-pill-container hover:bg-white/10 transition-all border border-white/10 flex items-center gap-1.5 shrink-0"
            >
              <span>Seed Demo Data</span>
            </button>
          )}
        </div>
      </div>

      {/* Grey.co-style 4 Top Statistic Metric Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div className="glass-card glass-card-interactive p-6 rounded-3xl border border-white/[0.08] relative overflow-hidden">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-[11px] font-extrabold text-slate-400 uppercase tracking-wider">Registered Items</h3>
            <span className="w-8 h-8 rounded-xl bg-orange-500/10 border border-orange-500/20 text-orange-400 flex items-center justify-center">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                <line x1="12" y1="22.08" x2="12" y2="12"/>
              </svg>
            </span>
          </div>
          <p className="text-4xl font-extrabold text-white tracking-tight">{stats.totalItems}</p>
          <div className="flex items-center justify-between text-xs text-slate-400 mt-3 pt-3 border-t border-white/[0.06]">
            <span>Active Machines & Assets</span>
            <span className="text-orange-400 font-bold">+100% Tracked</span>
          </div>
        </div>

        <div className="glass-card glass-card-interactive p-6 rounded-3xl border border-white/[0.08] relative overflow-hidden">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-[11px] font-extrabold text-emerald-400 uppercase tracking-wider">Verified Repairs</h3>
            <span className="w-8 h-8 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
              </svg>
            </span>
          </div>
          <p className="text-4xl font-extrabold text-white tracking-tight">{stats.totalRepairs}</p>
          <div className="flex items-center justify-between text-xs text-slate-400 mt-3 pt-3 border-t border-white/[0.06]">
            <span>Immutable Service Logs</span>
            <span className="text-emerald-400 font-bold">Tamper-Proof</span>
          </div>
        </div>

        <div className="glass-card glass-card-interactive p-6 rounded-3xl border border-white/[0.08] relative overflow-hidden">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-[11px] font-extrabold text-amber-400 uppercase tracking-wider">Pending Claims</h3>
            <span className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </span>
          </div>
          <p className="text-4xl font-extrabold text-white tracking-tight">{stats.openClaims}</p>
          <div className="flex items-center justify-between text-xs text-slate-400 mt-3 pt-3 border-t border-white/[0.06]">
            <span>Underwriter Review</span>
            <span className="text-amber-400 font-bold">Fast Settlement</span>
          </div>
        </div>

        <div className="glass-card glass-card-interactive p-6 rounded-3xl border border-white/[0.08] relative overflow-hidden">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-[11px] font-extrabold text-rose-400 uppercase tracking-wider">Open Incidents</h3>
            <span className="w-8 h-8 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-400 flex items-center justify-center">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </span>
          </div>
          <p className="text-4xl font-extrabold text-white tracking-tight">{stats.openIncidents}</p>
          <div className="flex items-center justify-between text-xs text-slate-400 mt-3 pt-3 border-t border-white/[0.06]">
            <span>Active Diagnostics</span>
            <span className="text-rose-400 font-bold">Assigned</span>
          </div>
        </div>
      </div>

      {/* Visual Analytics & Financial Assurance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Fraud Prevention Gauge */}
        <div className="glass-card p-6 lg:p-8 rounded-3xl border border-white/[0.08] flex flex-col justify-between">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-orange-500/15 text-orange-400 border border-orange-500/30 uppercase tracking-wider mb-2">
              AUDIT RELIABILITY
            </div>
            <h3 className="text-xl font-extrabold text-white tracking-tight mb-1">Fraud Prevention Rate</h3>
            <p className="text-xs text-slate-400">Repairs cryptographically attested via live QR & Photo verification</p>
          </div>

          <div className="relative flex items-center justify-center my-6">
            <svg className="w-48 h-48" viewBox="0 0 36 36">
              <path
                className="text-slate-800"
                strokeWidth="3.2"
                stroke="currentColor"
                fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className="text-orange-500"
                strokeDasharray="99, 100"
                strokeWidth="3.2"
                strokeLinecap="round"
                stroke="currentColor"
                fill="none"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
            <div className="absolute text-center">
              <span className="text-4xl font-black text-white tracking-tight">99%</span>
              <p className="text-[10px] text-orange-400 font-extrabold uppercase tracking-widest mt-1">Verified True</p>
            </div>
          </div>

          <div className="grid grid-cols-2 text-center pt-4 border-t border-white/[0.06] text-xs">
            <div className="border-r border-white/[0.06] pr-2">
              <span className="text-slate-400 font-medium block mb-0.5">Avg Repair Turnaround</span>
              <p className="font-extrabold text-white text-sm">2.4 Hours</p>
            </div>
            <div className="pl-2">
              <span className="text-slate-400 font-medium block mb-0.5">Audit Integrity</span>
              <p className="font-extrabold text-emerald-400 text-sm">100% Immutable</p>
            </div>
          </div>
        </div>

        {/* Claim Payout Health */}
        <div className="glass-card p-6 lg:p-8 rounded-3xl border border-white/[0.08] flex flex-col justify-between">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 uppercase tracking-wider mb-2">
              UNDERWRITER SETTLEMENTS
            </div>
            <h3 className="text-xl font-extrabold text-white tracking-tight mb-1">Insurance Claim Health</h3>
            <p className="text-xs text-slate-400 mb-6">Attested claim approval rate by hardware category</p>
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-xs font-bold mb-1.5">
                <span className="text-slate-300">Consumer Electronics</span>
                <span className="text-emerald-400">98% Approved</span>
              </div>
              <div className="w-full bg-[#0a0e17] h-2 rounded-full overflow-hidden border border-white/5">
                <div className="bg-emerald-500 h-2 rounded-full" style={{ width: '98%' }}></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs font-bold mb-1.5">
                <span className="text-slate-300">Vehicles & Automotive</span>
                <span className="text-orange-400">92% Approved</span>
              </div>
              <div className="w-full bg-[#0a0e17] h-2 rounded-full overflow-hidden border border-white/5">
                <div className="bg-gradient-to-r from-red-600 to-orange-500 h-2 rounded-full" style={{ width: '92%' }}></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs font-bold mb-1.5">
                <span className="text-slate-300">Home Appliances</span>
                <span className="text-emerald-400">95% Approved</span>
              </div>
              <div className="w-full bg-[#0a0e17] h-2 rounded-full overflow-hidden border border-white/5">
                <div className="bg-emerald-500 h-2 rounded-full" style={{ width: '95%' }}></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between text-xs font-bold mb-1.5">
                <span className="text-slate-300">Industrial & Telecom</span>
                <span className="text-amber-400">88% Approved</span>
              </div>
              <div className="w-full bg-[#0a0e17] h-2 rounded-full overflow-hidden border border-white/5">
                <div className="bg-amber-500 h-2 rounded-full" style={{ width: '88%' }}></div>
              </div>
            </div>
          </div>

          <div className="text-[10px] text-slate-400 mt-6 text-center font-mono">
            * Data backed by on-chain verification protocol
          </div>
        </div>

        {/* Quick Actions Panel */}
        <div className="glass-card p-6 lg:p-8 rounded-3xl border border-white/[0.08] flex flex-col justify-between">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-extrabold bg-white/10 text-slate-300 border border-white/15 uppercase tracking-wider mb-2">
              WORKSPACE SHORTCUTS
            </div>
            <h3 className="text-xl font-extrabold text-white tracking-tight mb-1">Platform Navigation</h3>
            <p className="text-xs text-slate-400 mb-6">Quick access to verified VERISURE modules</p>
          </div>

          <div className="space-y-3 flex-1">
            <button
              onClick={() => navigate('/tag')}
              className="w-full glass-card glass-card-interactive p-4 rounded-2xl text-left border border-orange-500/30 flex items-center gap-4 bg-orange-500/[0.04]"
            >
              <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-600 via-orange-500 to-amber-500 text-white flex items-center justify-center font-black text-xl shadow-md shadow-orange-500/20">
                +
              </span>
              <div>
                <div className="text-xs font-bold text-white">Register Device & Generate QR</div>
                <div className="text-[10px] text-orange-400">Direct self-registration & printable appliance badge</div>
              </div>
            </button>

            <button
              onClick={() => navigate('/items')}
              className="w-full glass-card glass-card-interactive p-4 rounded-2xl text-left border border-white/[0.06] flex items-center gap-4"
            >
              <span className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 text-orange-400 flex items-center justify-center">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                  <line x1="12" y1="22.08" x2="12" y2="12"/>
                </svg>
              </span>
              <div>
                <div className="text-xs font-bold text-white">Item Registry</div>
                <div className="text-[10px] text-slate-400">Manage tracked hardware & print QR cards</div>
              </div>
            </button>

            <button
              onClick={() => navigate('/qr-generator')}
              className="w-full glass-card glass-card-interactive p-4 rounded-2xl text-left border border-orange-500/30 flex items-center gap-4 bg-orange-500/[0.03]"
            >
              <span className="w-10 h-10 rounded-xl bg-orange-500/20 border border-orange-500/40 text-orange-400 flex items-center justify-center">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <rect x="7" y="7" width="3" height="3"/>
                  <rect x="14" y="7" width="3" height="3"/>
                  <rect x="7" y="14" width="3" height="3"/>
                  <rect x="14" y="14" width="3" height="3"/>
                </svg>
              </span>
              <div>
                <div className="text-xs font-bold text-orange-400">QR Identity Studio</div>
                <div className="text-[10px] text-orange-400/80">Generate & print official identity cards</div>
              </div>
            </button>

            <button
              onClick={() => navigate('/repair/log')}
              className="w-full glass-card glass-card-interactive p-4 rounded-2xl text-left border border-white/[0.06] flex items-center gap-4"
            >
              <span className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
                </svg>
              </span>
              <div>
                <div className="text-xs font-bold text-white">Log Certified Repair</div>
                <div className="text-[10px] text-slate-400">Submit verified technician report</div>
              </div>
            </button>

            {isAdmin && (
              <button
                onClick={() => navigate('/admin/blank-qr')}
                className="w-full glass-card glass-card-interactive p-4 rounded-2xl text-left border border-white/[0.06] flex items-center gap-4"
              >
                <span className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 flex items-center justify-center">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/>
                    <line x1="7" y1="7" x2="7.01" y2="7"/>
                  </svg>
                </span>
                <div>
                  <div className="text-xs font-bold text-white">Mint Outlet Blank QRs</div>
                  <div className="text-[10px] text-slate-400">Bulk issue unassigned stickers to outlets</div>
                </div>
              </button>
            )}

            {isAdmin && (
              <button
                onClick={() => navigate('/admin/blank-qr')}
                className="w-full glass-card glass-card-interactive p-4 rounded-2xl text-left border border-white/[0.06] flex items-center gap-4"
              >
                <span className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 text-orange-400 flex items-center justify-center">
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="6 9 6 2 18 2 18 9"/>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
                    <rect x="6" y="14" width="12" height="8"/>
                  </svg>
                </span>
                <div>
                  <div className="text-xs font-bold text-white">Bulk Print Appliance Stickers</div>
                  <div className="text-[10px] text-slate-400">Print transparent vinyl QR sheets for devices</div>
                </div>
              </button>
            )}
          </div>
        </div>

      </div>

      <PurgeMockDataModal
        isOpen={isPurgeModalOpen}
        onClose={() => setIsPurgeModalOpen(false)}
        onSuccess={fetchStats}
      />
    </div>
  );
};
